import React, { useState } from 'react';
import { Alert, Card, Col, Row, Statistic, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import salesConversionReportsService from '@/services/salesConversionReports.service';
import {
  defaultSalesReportFilters,
  SalesReportFilterBar,
} from './SalesReportFilterBar';

const { Title, Paragraph } = Typography;

const SalesConversionFunnelPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const [filters, setFilters] = useState(defaultSalesReportFilters());

  const { data, isLoading } = useQuery({
    queryKey: ['sales-conversion-funnel', filters],
    queryFn: () => salesConversionReportsService.conversionFunnel(filters),
  });

  const stages = (data?.stages as Array<Record<string, unknown>>) || [];
  const pairwise = (data?.pairwise as Array<Record<string, unknown>>) || [];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Conversion funnel</Title>
      <Paragraph type="secondary">
        Stage volumes with pairwise conversion rates. See also{' '}
        <Link to="/growth/dashboard?tab=analytics">Sales Analytics</Link>.
      </Paragraph>
      <SalesReportFilterBar
        value={filters}
        onChange={setFilters}
        showFunnelMode
        showCompare
      />
      {data?.notes ? <Alert type="info" showIcon style={{ marginBottom: 16 }} message={String(data.notes)} /> : null}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {stages.slice(0, 4).map((s) => (
          <Col xs={24} sm={12} md={6} key={String(s.stage)}>
            <Card size="small" loading={isLoading}>
              <Statistic
                title={String(s.stage)}
                value={Number(s.count || 0)}
                suffix={s.conversion_pct_from_prior != null ? `(${s.conversion_pct_from_prior}%)` : undefined}
              />
              <div style={{ marginTop: 4, color: '#666' }}>{formatCurrency(Number(s.value || 0))}</div>
            </Card>
          </Col>
        ))}
      </Row>
      <Card title="Pairwise conversion">
        <Table
          loading={isLoading}
          rowKey={(r) => `${r.from_stage}-${r.to_stage}-${r.method}`}
          size="small"
          dataSource={pairwise}
          pagination={false}
          columns={[
            { title: 'From', dataIndex: 'from_stage' },
            { title: 'To', dataIndex: 'to_stage' },
            { title: 'Method', dataIndex: 'method', width: 80 },
            { title: 'From #', dataIndex: 'count_from', align: 'right' },
            { title: 'To #', dataIndex: 'count_to', align: 'right' },
            {
              title: 'Conv %',
              dataIndex: 'conversion_pct',
              align: 'right',
              render: (v) => (v == null ? '—' : `${v}%`),
            },
            {
              title: 'Prior %',
              dataIndex: 'prior_conversion_pct',
              align: 'right',
              render: (v) => (v == null ? '—' : `${v}%`),
            },
            {
              title: 'Δ pp',
              dataIndex: 'change_pct_points',
              align: 'right',
              render: (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}`),
            },
            {
              title: 'From value',
              dataIndex: 'value_from',
              align: 'right',
              render: (v) => formatCurrency(Number(v || 0)),
            },
            {
              title: 'To value',
              dataIndex: 'value_to',
              align: 'right',
              render: (v) => formatCurrency(Number(v || 0)),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default SalesConversionFunnelPage;
